import type { LiveSessionRow } from "../controllers/live-sessions.ts";

export function statusLabel(status: string): string {
  switch (status) {
    case "active":
      return "Observing";
    case "idle":
      return "Takeover idle";
    case "paused":
      return "Paused";
    case "ended":
      return "Ended";
    case "aborted":
      return "Aborted";
    default:
      return "Pending";
  }
}

export function phaseLabel(phase: string): string {
  switch (phase) {
    case "monitoring":
      return "Monitoring desktop";
    case "setup":
      return "Connecting";
    case "review":
      return "Review";
    case "complete":
      return "Complete";
    default:
      return phase;
  }
}

export function formatDuration(minutes?: number): string {
  if (minutes == null || minutes < 1) {
    return "< 1 min";
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function formatPollInterval(ms?: number): string {
  if (!ms) {
    return "500 ms";
  }
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)} s`;
  }
  return `${ms} ms`;
}

/** One-line summary of what the session is doing right now. */
export function describeSessionActivity(session: LiveSessionRow): string {
  if (session.status === "ended" || session.status === "aborted") {
    return "Session finished. Desktop observation is no longer running.";
  }
  if (session.status === "paused") {
    return "Observation paused. The gateway is not polling the worker desktop.";
  }
  if (session.status === "pending") {
    return "Session created. Start the session to begin desktop observation.";
  }
  if (!session.observerBound) {
    return "Waiting for the observer subagent to bind before desktop polling begins.";
  }
  if (session.takeoverPerformed) {
    return "Worker appears away. Desktop takeover was performed; session is in idle act mode.";
  }
  if (session.humanPresent === false) {
    return "Worker appears away from the desktop. Takeover may be triggered on the next poll.";
  }

  const focus = session.focusedWindow?.trim();
  const count = session.windowCount;
  if (focus && count != null) {
    return `Watching ${count} window${count === 1 ? "" : "s"} on the worker machine. Focus is on “${focus}”.`;
  }
  if (session.recentSkills && session.recentSkills.length > 0) {
    return `Watching the worker desktop. Recently seen: ${session.recentSkills.slice(0, 3).join(", ")}.`;
  }
  return "Polling the worker desktop for open applications and feeding the observer agent.";
}

export function humanPresenceLabel(session: LiveSessionRow): string | null {
  if (session.status !== "active" && session.status !== "idle") {
    return null;
  }
  if (session.takeoverPerformed) {
    return "Takeover done";
  }
  if (session.humanPresent === false) {
    return "Human away";
  }
  if (session.humanPresent === true) {
    return "Human present";
  }
  return "Presence unknown";
}
